export interface NormalCurveConfig {
  mu: number;
  sigma: number;
  color?: string;
  lineStyle?: 'solid' | 'dashed';
  lineWidth?: number;
  areaFill?: boolean;
}

export interface ShadedRegion {
  /** Left bound of the shaded area (use xMin for left tail). */
  from: number;
  /** Right bound of the shaded area (use xMax for right tail). */
  to: number;
  /** Fill color with opacity, e.g. 'rgba(99, 102, 241, 0.3)'. Defaults to curve color at 0.3 opacity. */
  color?: string;
}

export interface TopLabelConfig {
  value: number;
  text: string;
  /** Line color override. Defaults to the first curve's color. */
  color?: string;
  /** Line style. Defaults to 'dashed'. */
  lineType?: 'solid' | 'dashed';
  /** Line width. Defaults to 1. */
  lineWidth?: number;
  /**
   * How tall the vertical line extends:
   * - 'curve' (default): line goes up to the curve height at this x-value
   * - 'full': line extends the full chart height, label at the very top
   */
  extent?: 'curve' | 'full';
}

export interface NormalDistributionChartParams {
  curves: NormalCurveConfig[];
  xMin: number;
  xMax: number;
  xAxisLabel?: string;
  yAxisLabel?: string;
  showYAxis?: boolean;
  numPoints?: number;
  showEmpiricalRule?: boolean;
  /** Shaded regions under the first curve. */
  shadedRegions?: ShadedRegion[];
  /** X-values with dashed lines and labels in the first curve's color. Use a number for the raw value, or { value, text } for custom label text. */
  topLabels?: Array<number | TopLabelConfig>;
  /** When true, shade the complement of the shaded regions in a lighter color. */
  shadeComplement?: boolean;
  /** When false, hides the auto-generated x-axis tick labels. Defaults to true. */
  showAxisLabels?: boolean;
  /** Text labels placed at data coordinates on the chart. Rendered as KaTeX. `dx` shifts the label by a fixed number of pixels (positive = right). `align` overrides the auto-determined text alignment. */
  annotations?: Array<{
    x: number;
    y: number;
    text: string;
    color?: string;
    dx?: number;
    align?: 'left' | 'center' | 'right';
  }>;
  /** Override the right grid padding (in px). Defaults to 8. Increase when the rightmost x-axis label is clipped. */
  gridRight?: number;
  /** Centered title rendered as KaTeX above the chart area. */
  title?: { text: string; color?: string };
}

export type { NormalChartPreset } from './normal-distribution-chart.presets';
export { resolvePreset } from './normal-distribution-chart.presets';
